import React from 'react';
import { FiInfo, FiTag } from 'react-icons/fi';
import type {
  CampaignMasterRow,
  LeadSource,
  MarketingCampaignStatus,
} from '@troia-v3/types';
import { getLeadSourceLabel } from '@troia-v3/types';

import { LeadSourceIcon } from '@/shared/icons/LeadSourceIcon';
import { DashboardSectionCard } from './DashboardSectionCard';

interface CampaignsMasterTableProps {
  rows: CampaignMasterRow[];
  noAdSpendData?: boolean;
}

const TROIA_PURPLE_DARK = '#7c3aed';

const STATUS_META: Record<MarketingCampaignStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Ativa', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  paused: { label: 'Pausada', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  ended: { label: 'Encerrada', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' },
  continuous: { label: 'Contínua', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
};

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

const formatBRL = (v: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(v);

const formatPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;

const formatDaysAgo = (days: number): string => {
  if (days === 0) return 'hoje';
  if (days === 1) return 'há 1 dia';
  return `há ${days} dias`;
};

const StatusPill: React.FC<{ status: MarketingCampaignStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      <span
        className="rounded-full"
        style={{ width: 6, height: 6, backgroundColor: 'currentColor' }}
      />
      {meta.label}
    </span>
  );
};

const SourcePill: React.FC<{ source: LeadSource }> = ({ source }) => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
    style={{
      backgroundColor: 'var(--theme-page-background)',
      color: 'var(--theme-card-text)',
    }}
  >
    <LeadSourceIcon source={source} size={11} color="var(--theme-muted-text)" />
    {getLeadSourceLabel(source)}
  </span>
);

/**
 * Regra híbrida C: roxo padrão pra ROAS bom/médio. Vermelho discreto APENAS
 * pra ROAS crítico (<3x). `muted` = sem dados de ad spend → "—".
 */
const RoasPill: React.FC<{ roas: number; muted: boolean }> = ({ roas, muted }) => {
  if (muted) {
    return <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>;
  }
  const isCritical = roas < 3;
  const color = isCritical ? '#b91c1c' : roas >= 5 ? '#6d28d9' : TROIA_PURPLE_DARK;
  const bg = isCritical
    ? 'rgba(239, 68, 68, 0.12)'
    : roas >= 5
      ? 'rgba(139, 92, 246, 0.18)'
      : 'rgba(139, 92, 246, 0.10)';
  return (
    <span
      className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: bg, color }}
    >
      {roas.toFixed(1)}x
    </span>
  );
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-card-background)',
  color: 'var(--theme-muted-text)',
  borderBottom: '1px solid var(--theme-card-border)',
};

const cellStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--theme-card-border)',
  color: 'var(--theme-card-text)',
};

/** Card mobile de campanha (tabela vira cards abaixo de md). */
const CampaignMobileCard: React.FC<{ row: CampaignMasterRow; noAdSpendData: boolean }> = ({ row, noAdSpendData }) => {
  const faded = row.status === 'paused' || row.status === 'ended';
  return (
    <div
      className="rounded-lg p-3 flex flex-col gap-2.5"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        border: '1px solid var(--theme-card-border)',
        opacity: faded ? 0.7 : 1,
      }}
      data-testid={`campaign-card-${row.id}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="text-xs font-semibold flex-shrink-0"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          #{row.rank}
        </span>
        <span
          className="text-sm font-semibold flex-1 min-w-0 truncate"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {row.name}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusPill status={row.status} />
        <SourcePill source={row.source} />
        <RoasPill roas={row.roas} muted={noAdSpendData} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { k: 'Leads', v: formatNumber(row.leads) },
          { k: 'Propostas', v: formatNumber(row.proposals) },
          { k: 'Won', v: formatNumber(row.won) },
          { k: 'Receita', v: formatBRL(row.revenue) },
        ].map((cell) => (
          <div
            key={cell.k}
            className="rounded-md px-2.5 py-1.5"
            style={{ backgroundColor: 'var(--theme-page-background)' }}
          >
            <div className="text-[10px]" style={{ color: 'var(--theme-muted-text)' }}>{cell.k}</div>
            <div
              className="text-sm font-bold mt-0.5"
              style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
            >
              {cell.v}
            </div>
          </div>
        ))}
      </div>
      <div
        className="text-xs flex flex-wrap gap-x-3 gap-y-1"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {!noAdSpendData && <span>Spend {formatBRL(row.spend)}</span>}
        <span>L→Q {formatPct(row.leadToQualifiedPct)}</span>
        <span>Q→P {formatPct(row.qualifiedToProposalsPct)}</span>
        <span>P→W {formatPct(row.proposalsToWonPct)}</span>
        <span>{formatDaysAgo(row.daysSinceLastActivity)}</span>
      </div>
    </div>
  );
};

const NoSpendBadge: React.FC = () => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full"
    style={{ backgroundColor: 'var(--theme-page-background)', color: 'var(--theme-muted-text)' }}
    title="Sem integração com plataformas de ads — Spend e ROAS estão zerados."
  >
    <FiInfo size={11} />
    sem spend / ROAS
  </span>
);

export const CampaignsMasterTable: React.FC<CampaignsMasterTableProps> = ({
  rows,
  noAdSpendData = false,
}) => {
  const header = (
    <DashboardSectionCard
      icon={<FiTag size={16} style={{ color: 'var(--theme-card-text)' }} />}
      title="Campanhas"
      subtitle="Desempenho por campanha no período"
      badge={noAdSpendData ? <NoSpendBadge /> : undefined}
      flush
      testId="campaigns-master-table"
    >
      {rows.length === 0 ? (
        <div
          className="m-4 rounded-lg p-8 text-center text-sm"
          style={{
            border: '1px dashed var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
          data-testid="campaigns-master-table-empty"
        >
          Nenhuma campanha com leads no período.
        </div>
      ) : (
        <>
          {/* Tabela (md+) */}
          <div className="hidden min-[900px]:block" style={{ overflowX: 'auto' }}>
            <table className="w-full text-xs" style={{ minWidth: 1200, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>#</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Campanha</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Status</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Origem</th>
                  {!noAdSpendData && (
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Spend</th>
                  )}
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Leads</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Qualif.</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>L→Q</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Propostas</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Q→P</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Won</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>P→W</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Receita</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>ROAS</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wide font-bold" style={headerCellStyle}>Última atividade</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const fadedStyle: React.CSSProperties = row.status === 'paused' || row.status === 'ended'
                    ? { opacity: 0.65 }
                    : {};
                  return (
                    <tr key={row.id} style={fadedStyle} data-testid={`campaign-row-${row.id}`}>
                      <td className="px-3 py-2" style={{ ...cellStyle, color: 'var(--theme-muted-text)' }}>{row.rank}</td>
                      <td className="px-3 py-2 font-semibold" style={cellStyle}>{row.name}</td>
                      <td className="px-3 py-2" style={cellStyle}><StatusPill status={row.status} /></td>
                      <td className="px-3 py-2" style={cellStyle}><SourcePill source={row.source} /></td>
                      {!noAdSpendData && (
                        <td className="px-3 py-2 text-right" style={cellStyle}>{formatBRL(row.spend)}</td>
                      )}
                      <td className="px-3 py-2 text-right" style={cellStyle}>{formatNumber(row.leads)}</td>
                      <td className="px-3 py-2 text-right" style={cellStyle}>{formatNumber(row.qualified)}</td>
                      <td className="px-3 py-2 text-right" style={{ ...cellStyle, color: 'var(--theme-muted-text)' }}>{formatPct(row.leadToQualifiedPct)}</td>
                      <td className="px-3 py-2 text-right" style={cellStyle}>{formatNumber(row.proposals)}</td>
                      <td className="px-3 py-2 text-right" style={{ ...cellStyle, color: 'var(--theme-muted-text)' }}>{formatPct(row.qualifiedToProposalsPct)}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={cellStyle}>{formatNumber(row.won)}</td>
                      <td className="px-3 py-2 text-right" style={{ ...cellStyle, color: 'var(--theme-muted-text)' }}>{formatPct(row.proposalsToWonPct)}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={cellStyle}>{formatBRL(row.revenue)}</td>
                      <td className="px-3 py-2 text-center" style={cellStyle}><RoasPill roas={row.roas} muted={noAdSpendData} /></td>
                      <td className="px-3 py-2" style={{ ...cellStyle, color: 'var(--theme-muted-text)' }}>{formatDaysAgo(row.daysSinceLastActivity)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cards (mobile) */}
          <div className="min-[900px]:hidden flex flex-col gap-2.5 p-4">
            {rows.map((row) => (
              <CampaignMobileCard key={row.id} row={row} noAdSpendData={noAdSpendData} />
            ))}
          </div>
        </>
      )}
    </DashboardSectionCard>
  );

  return header;
};

CampaignsMasterTable.displayName = 'CampaignsMasterTable';
